/**
 * Calculates the yaw angle (heading/compass direction) between two points in EUS coordinates.
 * The angle represents the direction from the starting point to the ending point,
 *
 * Visual representation of the angle system:
 *
 *            North (90° | π/2)
 *                    ^
 *                    │
 *                    │
 * West (180° | π) <──┼──> East (0°)
 *                    │
 *                    │
 *                    v
 *            South (270° | 3π/2)
 *
 * @param fromPos - Starting position [E, U, S]
 * @param toPos - Ending position [E, U, S]
 * @returns Yaw angle in radians, measured clockwise from East
 */
export const calculateYawAngle = (
  fromPos: [number, number, number],
  toPos: [number, number, number]
): number => {
  // Calculate deltas from fromPos to toPos
  const deltaE = toPos[0] - fromPos[0]; // East delta
  const deltaS = toPos[2] - fromPos[2]; // South delta

  // Calculate angle - we need to invert South because on maps North is up
  // Math.atan2(y, x) gives the angle counterclockwise from the positive x-axis
  // By using -deltaS, we're converting from South to North direction
  return normalizeAngle(Math.atan2(-deltaS, deltaE));
};

/**
 * Normalizes an angle to be within the range [0, 2π).
 * @param angle - The angle to normalize in radians.
 * @returns The normalized angle in radians.
 */
function normalizeAngle(angle: number): number {
  // Returns angle in [0, 2π)
  return ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
}

/**
 * Quantizes an angle to a given number of steps.
 * @param angle The angle to quantize.
 * @param steps The number of steps to quantize to.
 * @returns The quantized angle.
 *
 * By default, we get:
 * 12 steps -> 30 degrees (0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330)
 * Like clocks.
 */
export function quantizeAngle(angle: number, steps: number = 12) {
  const stepSize = Math.PI / steps;
  return Math.round(angle / stepSize) * stepSize;
}

/**
 * Calculates the pitch angle based on horizontal distance and height difference.
 * A negative angle means pointing downward, 0 means horizontal, positive means pointing upward.
 *
 * Visual representation of the pitch angle:
 *
 *    Height
 *      ^
 *      │  > (positive pitch - up)
 *      │ ╱
 *      │╱
 *      │───────> Distance
 *      │╲
 *      │ ╲
 *      │  > (negative pitch - down)
 *
 * @param fromPos - Starting position [E, U, S]
 * @param toPos - Ending position [E, U, S]
 * @returns Pitch angle in radians (negative for downward, 0 for horizontal, positive for upward)
 */
export const calculatePitchAngle = (
  fromPos: [number, number, number],
  toPos: [number, number, number]
): number => {
  // Calculate horizontal distance between points (in E-S plane)
  const deltaE = toPos[0] - fromPos[0]; // East delta
  const deltaS = toPos[2] - fromPos[2]; // South delta
  const distance = Math.sqrt(deltaE * deltaE + deltaS * deltaS);

  // Calculate total height difference
  const totalHeightDiff = toPos[1] - fromPos[1];

  // If distance is 0, we're either pointing straight up or down
  if (distance === 0) {
    return normalizeAngle(totalHeightDiff >= 0 ? Math.PI / 2 : -Math.PI / 2);
  }

  // Calculate the pitch angle using arctangent
  // Math.atan2 gives us the angle between the x-axis and the point (distance, height)
  // A negative height will result in a negative angle (pointing down)
  return normalizeAngle(Math.atan2(totalHeightDiff, distance));
};

interface PointWithHeight {
  pos: [number, number, number]; // [E, U, S]
  height: number;
}

/**
 * Calculates a point that is a specified distance from pointA toward pointB,
 * but not further than halfway between the two points.
 *
 *    PointA            PointC             PointB
 *      o-----------------o------------------o
 *            distance
 *      |<--------------->|
 *
 * @param pointA - Starting point with position and height
 * @param pointB - Target point with position and height
 * @param distance - Distance to move from pointA toward pointB (in meters)
 * @returns New point that is the specified distance from pointA toward pointB
 */
export const calculateIntermediatePoint = (
  pointA: PointWithHeight,
  pointB: PointWithHeight,
  distance: number
): PointWithHeight => {
  // Calculate vector from pointA to pointB
  const vectorAB: [number, number, number] = [
    pointB.pos[0] - pointA.pos[0],
    pointB.pos[1] - pointA.pos[1],
    pointB.pos[2] - pointA.pos[2],
  ];

  // Calculate the total distance between pointA and pointB
  const totalDistance = Math.sqrt(
    vectorAB[0] * vectorAB[0] + vectorAB[1] * vectorAB[1] + vectorAB[2] * vectorAB[2]
  );

  // If points are the same or very close, return pointA
  if (totalDistance < 0.001) {
    return { ...pointA };
  }

  // Calculate the maximum allowed distance (halfway point)
  const maxDistance = totalDistance / 2;

  // Use the smaller of the requested distance and the maximum allowed distance
  const actualDistance = Math.min(distance, maxDistance);

  // Calculate the ratio of the actual distance to the total distance
  const ratio = actualDistance / totalDistance;

  // Calculate the new point by moving from pointA toward pointB by the ratio
  return {
    pos: [
      pointA.pos[0] + vectorAB[0] * ratio,
      pointA.pos[1] + vectorAB[1] * ratio,
      pointA.pos[2] + vectorAB[2] * ratio,
    ],
    height: pointA.height + (pointB.height - pointA.height) * ratio,
  };
};
